import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SimuladorProvider } from './context/SimuladorContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { Toaster } from 'sonner';
import { setTimezone, setDateFormat, setTimeFormat, setAllFormats } from './lib/utils';
import axios from 'axios';
import OfflineAlert from './components/OfflineAlert';
import { GestorShell } from './components/GestorLayout';
import Login from './pages/Login';
import OperadorLogin from './pages/operador/OperadorLogin';
import OperadorHome from './pages/operador/OperadorHome';
import ProducaoAtiva from './pages/operador/ProducaoAtiva';
import FimDia from './pages/operador/FimDia';
import ResgatesOperador from './pages/operador/ResgatesOperador';
import GestorDashboard from './pages/gestor/GestorDashboard';
import Planeamento from './pages/gestor/Planeamento';
import BolsaTarefas from './pages/gestor/BolsaTarefas';
import GestaoOFs from './pages/gestor/GestaoOFs';
import GestaoOperadores from './pages/gestor/GestaoOperadores';
import GestaoMarcas from './pages/gestor/GestaoMarcas';
import GestaoOperacoes from './pages/gestor/GestaoOperacoes';
import GestaoCategorias from './pages/gestor/GestaoCategorias';
import GestaoAnomalias from './pages/gestor/GestaoAnomalias';
import DashboardAnomalias from './pages/gestor/DashboardAnomalias';
import GestaoAlertas from './pages/gestor/GestaoAlertas';
import SistemaStatus from './pages/gestor/SistemaStatus';
import SimuladorOperadores from './pages/gestor/SimuladorOperadores';
import Financeiro from './pages/gestor/Financeiro';
import RelatoriosV2 from './pages/gestor/RelatoriosV2';
import RelatorioOperador from './pages/gestor/RelatorioOperador';
import RelatorioOF from './pages/gestor/RelatorioOF';
import Configuracoes from './pages/gestor/Configuracoes';
import Producao from './components/Producao';
import EstadoOperadores from './pages/gestor/EstadoOperadores';
import GestaoFerias from './pages/gestor/GestaoFerias';
import Performance from './pages/gestor/Performance';
import GestaoUtilizadores from './pages/gestor/GestaoUtilizadores';
import Regras from './pages/gestor/Regras';
import ApiDocumentation from './pages/gestor/ApiDocumentation';
import RegrasNotificacoes from './pages/gestor/RegrasNotificacoes';
import SistemasPage from './pages/gestor/SistemasPage';
import AdminOpenAI from './pages/gestor/AdminOpenAI';
import TVDashboard from './pages/TVDashboard';
import CartazPontuais from './pages/CartazPontuais';
import SelecionarOperadorPontuais from './pages/operador/SelecionarOperadorPontuais';
import './App.css';

const PrivateRoute = ({ children, requiredPerfil }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admin tem acesso a TUDO (gestor + operador)
  const userRole = user.role || user.perfil;
  if (userRole === 'admin') return children;

  if (requiredPerfil && user.perfil !== requiredPerfil) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Hook para esconder badge "Made with Emergent" quando em modo iframe
const EmergentBadgeHandler = ({ children }) => {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('hideEmergentBadge') === 'true') {
      document.body.classList.add('hide-emergent-badge');
    }
    return () => {
      document.body.classList.remove('hide-emergent-badge');
    };
  }, []);
  return children;
};

/**
 * TarefasPontuaisRedirect
 * Quando o operador escaneia o QR Code, chega a /tarefas-pontuais.
 * - Se está autenticado como operador → abre OperadorHome com tab pontuais
 * - Se não está autenticado → mostra lista de operadores para seleccionar
 */
const TarefasPontuaisRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Se autenticado como operador ou admin → abrir tarefas pontuais
  const userRole = user?.role || user?.perfil;
  if (user && (userRole === 'operador' || userRole === 'admin')) {
    return <OperadorHome abrirTarefasPontuais={true} />;
  }

  // Não autenticado → mostrar selecção rápida de operador
  return <SelecionarOperadorPontuais />;
};

// Hook para carregar configurações gerais (timezone, formatos) ao iniciar
const ConfigLoader = ({ children }) => {
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${API}/api/configuracoes/geral`);
        if (res.data) {
          setAllFormats(res.data);
        }
      } catch (error) {
        // Usar valores por defeito se falhar
        console.log('A usar configurações por defeito');
      }
    };
    loadConfig();
    
    // Escutar mudanças de configurações (quando o utilizador altera nas configurações)
    const handleConfigChange = (event) => {
      if (event.detail) {
        if (event.detail.timezone) setTimezone(event.detail.timezone);
        if (event.detail.formato_data) setDateFormat(event.detail.formato_data);
        if (event.detail.formato_hora) setTimeFormat(event.detail.formato_hora);
      }
    };
    window.addEventListener('configChanged', handleConfigChange);
    // Manter compatibilidade com evento antigo
    window.addEventListener('timezoneChanged', (e) => setTimezone(e.detail));
    
    return () => {
      window.removeEventListener('configChanged', handleConfigChange);
      window.removeEventListener('timezoneChanged', (e) => setTimezone(e.detail));
    };
  }, []);
  
  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  // Mostra loading enquanto verifica autenticação
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // Determinar userId e perfil para o WebSocket
  const wsUserId = user?.id || user?.username || null;
  const wsPerfil = user?.role || user?.perfil || 'operador';

  const routes = (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      {/* Redirect da home baseado no perfil */}
      <Route 
        path="/" 
        element={
          user ? (
            (user.role === 'admin' || user.role === 'gestor' || user.perfil === 'gestor') ? 
              <Navigate to="/gestor/dashboard" replace /> : 
              <Navigate to="/operador" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        } 
      />

      {/* Rotas do Operador */}
      <Route path="/operador/login" element={<Login />} />
      <Route
        path="/operador"
        element={
          <PrivateRoute requiredPerfil="operador">
            <OperadorHome />
          </PrivateRoute>
        }
      />
      <Route
        path="/operador/producao"
        element={
          <PrivateRoute requiredPerfil="operador">
            <ProducaoAtiva />
          </PrivateRoute>
        }
      />
      <Route
        path="/operador/fim-dia"
        element={
          <PrivateRoute requiredPerfil="operador">
            <FimDia />
          </PrivateRoute>
        }
      />
      <Route
        path="/operador/resgates"
        element={
          <PrivateRoute requiredPerfil="operador">
            <ResgatesOperador />
          </PrivateRoute>
        }
      />
      
      {/* Rota especial para QR Code de Tarefas Pontuais */}
      {/* Sem PrivateRoute — o operador pode não estar autenticado via JWT */}
      {/* Redireciona para login do operador com flag para abrir tarefas pontuais */}
      <Route
        path="/tarefas-pontuais"
        element={<TarefasPontuaisRedirect />}
      />

      {/* Rotas de Simulação de Operador (sem autenticação - apenas para gestor) */}
      <Route path="/sim/operador/:username" element={<EmergentBadgeHandler><OperadorHome /></EmergentBadgeHandler>} />
      <Route path="/sim/operador/:username/resgates" element={<EmergentBadgeHandler><ResgatesOperador /></EmergentBadgeHandler>} />
      <Route path="/sim/operador/:username/fim-dia" element={<EmergentBadgeHandler><FimDia /></EmergentBadgeHandler>} />
      <Route path="/sim/operador/:username/producao" element={<EmergentBadgeHandler><ProducaoAtiva /></EmergentBadgeHandler>} />

      {/* Dashboard TV (sem autenticação) */}
      <Route path="/tv" element={<TVDashboard />} />

      {/* Cartaz A6 para impressao — QR Code Tarefas Pontuais (sem autenticação) */}
      <Route path="/cartaz-pontuais" element={<CartazPontuais />} />

      {/* Rotas do Gestor — layout persistente (GestorShell) partilhado por todas as páginas */}
      <Route
        element={
          <PrivateRoute requiredPerfil="gestor">
            <GestorShell />
          </PrivateRoute>
        }
      >
        <Route path="/gestor/dashboard" element={<GestorDashboard />} />
        <Route path="/gestor/planeamento" element={<Planeamento />} />
        <Route path="/gestor/bolsa-tarefas" element={<BolsaTarefas />} />
        <Route path="/gestor/ofs" element={<GestaoOFs />} />
        <Route path="/gestor/operadores" element={<GestaoOperadores />} />
        <Route path="/gestor/marcas" element={<GestaoMarcas />} />
        <Route path="/gestor/operacoes" element={<GestaoOperacoes />} />
        <Route path="/gestor/categorias" element={<GestaoCategorias />} />
        <Route path="/gestor/anomalias" element={<DashboardAnomalias />} />
        <Route path="/gestor/alertas" element={<GestaoAlertas />} />
        <Route path="/gestor/financeiro" element={<Financeiro />} />
        <Route path="/gestor/simulador" element={<SimuladorOperadores />} />
        <Route path="/gestor/relatorios" element={<RelatoriosV2 />} />
        <Route path="/gestor/relatorios/operador/:operadorId" element={<RelatorioOperador />} />
        <Route path="/gestor/relatorios/of/:ofId" element={<RelatorioOF />} />
        <Route path="/gestor/configuracoes" element={<Configuracoes />} />
        <Route path="/gestor/openai" element={<AdminOpenAI />} />
        <Route path="/gestor/estado-operadores" element={<EstadoOperadores />} />
        <Route path="/gestor/ferias" element={<GestaoFerias />} />
        <Route path="/gestor/performance" element={<Performance />} />
        <Route path="/gestor/utilizadores" element={<GestaoUtilizadores />} />
        <Route path="/gestor/regras" element={<Regras />} />
        <Route path="/gestor/sistemas" element={<SistemasPage />} />
        <Route path="/gestor/sistema" element={<SistemaStatus />} />
        <Route path="/gestor/api-docs" element={<ApiDocumentation />} />
        <Route path="/gestor/regras-notificacoes" element={<RegrasNotificacoes />} />
      </Route>
      {/* Redirects para compatibilidade */}
      <Route path="/gestor/ordens-fabrico" element={<Navigate to="/gestor/ofs" replace />} />
      <Route path="/gestor/resgates" element={<Navigate to="/gestor/anomalias" replace />} />
      {/* Produção (ecrã próprio, sem sidebar) */}
      <Route
        path="/gestor/producao"
        element={
          <PrivateRoute requiredPerfil="gestor">
            <Producao />
          </PrivateRoute>
        }
      />
    </Routes>
  );

  // Envolver com WebSocketProvider se o user está autenticado
  if (wsUserId) {
    return (
      <WebSocketProvider userId={wsUserId} perfil={wsPerfil}>
        {routes}
      </WebSocketProvider>
    );
  }

  return routes;
};

function App() {
  return (
    <AuthProvider>
      <SimuladorProvider>
        <ConfigLoader>
          <BrowserRouter>
            <OfflineAlert />
            <AppRoutes />
            <Toaster position="top-center" richColors closeButton toastOptions={{ style: { maxWidth: '520px', margin: '0 auto', fontSize: '1.05rem', padding: '16px 20px' } }} />
          </BrowserRouter>
        </ConfigLoader>
      </SimuladorProvider>
    </AuthProvider>
  );
}

export default App;
